import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calculator, DollarSign, TrendingUp, TrendingDown, Target, Shield, 
  PiggyBank, AlertTriangle, CheckCircle, XCircle, Plus, Trash2, 
  RotateCcw, Save, ChevronDown, ChevronUp, Trophy, Star, Zap,
  Building2, User, ArrowLeft, Wallet, BarChart3, Lightbulb, LineChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

// API URL helper
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Expense categories
const INDIVIDUAL_CATEGORIES = [
  'Housing', 'Food', 'Transport', 'Subscriptions', 'Debt', 
  'Utilities', 'Insurance', 'Entertainment', 'Healthcare', 'Other'
];

const BUSINESS_CATEGORIES = [
  'Payroll', 'Operating Costs', 'Rent', 'Software Tools', 'Inventory',
  'Business Debt', 'Marketing', 'Utilities', 'Insurance', 'Other'
];

// Level system
const LEVELS = [
  { min: 0, max: 20, level: 1, name: 'Budget Beginner', icon: '🌱', color: 'text-gray-500' },
  { min: 21, max: 40, level: 2, name: 'Stability Builder', icon: '🏗️', color: 'text-blue-500' },
  { min: 41, max: 60, level: 3, name: 'Cash Flow Controller', icon: '💪', color: 'text-green-500' },
  { min: 61, max: 80, level: 4, name: 'Financial Strategist', icon: '🎯', color: 'text-purple-500' },
  { min: 81, max: 100, level: 5, name: 'Wealth Architect', icon: '👑', color: 'text-yellow-500' },
];

const FinancialGamePlanner = ({ user, onBack }) => {
  // Disclaimer state
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    return localStorage.getItem('financialPlannerDisclaimerAccepted') === 'true';
  });
  
  // Plan type
  const [planType, setPlanType] = useState(null); // 'individual' or 'business'
  
  // Income state
  const [incomes, setIncomes] = useState([]);
  const [newIncome, setNewIncome] = useState({ name: '', amount: '', type: 'Fixed' });
  
  // Expense state
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ name: '', category: '', amount: '', type: 'Fixed' });
  
  // Savings & Investment state
  const [currentSavings, setCurrentSavings] = useState('');
  const [monthlySavingsContribution, setMonthlySavingsContribution] = useState('');
  const [monthlyInvestmentContribution, setMonthlyInvestmentContribution] = useState('');
  const [investmentReturnRate, setInvestmentReturnRate] = useState('20'); // Default 20% monthly
  const [yearlySavingsGoal, setYearlySavingsGoal] = useState('');
  
  // Calculated results
  const [results, setResults] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [score, setScore] = useState(null);
  
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    income: true,
    expenses: true,
    allocation: true,
    results: true,
    analysis: true,
    simulations: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Load saved data
  useEffect(() => {
    loadSavedData();
  }, []);
  
  // Auto-calculate when data changes
  useEffect(() => {
    if (planType && incomes.length > 0) {
      calculateResults();
    }
  }, [planType, incomes, expenses, currentSavings, monthlySavingsContribution, monthlyInvestmentContribution, investmentReturnRate, yearlySavingsGoal]);
  
  const loadSavedData = async () => {
    setLoading(true);
    const token = localStorage.getItem('guestToken');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/user/financial-plan?token=${token}`);
      const data = await response.json();
      
      if (data.success && data.plan) {
        const plan = data.plan;
        setPlanType(plan.plan_type || null);
        setIncomes(plan.incomes || []);
        setExpenses(plan.expenses || []);
        setCurrentSavings(plan.current_savings?.toString() || '');
        setMonthlySavingsContribution(plan.monthly_savings_contribution?.toString() || '');
        setMonthlyInvestmentContribution(plan.monthly_investment_contribution?.toString() || '');
        setInvestmentReturnRate(plan.investment_return_rate?.toString() || '20');
        setYearlySavingsGoal(plan.yearly_savings_goal?.toString() || '');
      }
    } catch (err) {
      console.error('Failed to load financial plan:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const saveData = async () => {
    const token = localStorage.getItem('guestToken');
    if (!token) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/user/financial-plan?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_type: planType,
          incomes,
          expenses,
          current_savings: parseFloat(currentSavings) || 0,
          monthly_savings_contribution: parseFloat(monthlySavingsContribution) || 0,
          monthly_investment_contribution: parseFloat(monthlyInvestmentContribution) || 0,
          investment_return_rate: parseFloat(investmentReturnRate) || 20,
          yearly_savings_goal: parseFloat(yearlySavingsGoal) || 0
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Financial plan saved!');
      } else {
        toast.error(data.message || 'Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save financial plan');
    } finally {
      setSaving(false);
    }
  };
  
  const resetPlan = async () => {
    if (!window.confirm('Are you sure you want to reset your entire financial plan? This cannot be undone.')) return;
    
    const token = localStorage.getItem('guestToken');
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/user/financial-plan/reset?token=${token}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        setPlanType(null);
        setIncomes([]);
        setExpenses([]);
        setCurrentSavings('');
        setMonthlySavingsContribution('');
        setMonthlyInvestmentContribution('');
        setInvestmentReturnRate('20');
        setYearlySavingsGoal('');
        setResults(null);
        setAnalysis(null);
        setScore(null);
        toast.success('Financial plan reset!');
      }
    } catch (err) {
      toast.error('Failed to reset plan');
    }
  };
  
  // Accept disclaimer
  const acceptDisclaimer = () => {
    localStorage.setItem('financialPlannerDisclaimerAccepted', 'true');
    setDisclaimerAccepted(true);
  };
  
  // Income management
  const addIncome = () => {
    if (!newIncome.name || !newIncome.amount) {
      toast.error('Please enter income name and amount');
      return;
    }
    
    const income = {
      id: Date.now(),
      name: newIncome.name,
      amount: parseFloat(newIncome.amount),
      type: newIncome.type
    };
    
    setIncomes([...incomes, income]);
    setNewIncome({ name: '', amount: '', type: 'Fixed' });
    toast.success('Income added!');
  };
  
  const removeIncome = (id) => {
    setIncomes(incomes.filter(i => i.id !== id));
  };
  
  // Expense management
  const addExpense = () => {
    if (!newExpense.name || !newExpense.amount || !newExpense.category) {
      toast.error('Please fill in all expense fields');
      return;
    }
    
    const expense = {
      id: Date.now(),
      name: newExpense.name,
      category: newExpense.category,
      amount: parseFloat(newExpense.amount),
      type: newExpense.type
    };
    
    setExpenses([...expenses, expense]);
    setNewExpense({ name: '', category: '', amount: '', type: 'Fixed' });
    toast.success('Expense added!');
  };
  
  const removeExpense = (id) => {
    setExpenses(expenses.filter(e => e.id !== id));
  };
  
  // Calculate all financial results
  const calculateResults = useCallback(() => {
    const totalIncome = incomes.reduce((sum, i) => sum + i.amount, 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
    const netCashFlow = totalIncome - totalExpenses;
    
    // Monthly allocations
    const savingsContrib = parseFloat(monthlySavingsContribution) || 0;
    const investmentContrib = parseFloat(monthlyInvestmentContribution) || 0;
    const totalAllocations = savingsContrib + investmentContrib;
    
    // Remaining after allocations
    const remainingCashFlow = netCashFlow - totalAllocations;
    
    // Investment calculations (assuming monthly return rate)
    const returnRate = (parseFloat(investmentReturnRate) || 20) / 100;
    const monthlyInvestmentReturn = investmentContrib * returnRate;
    const yearlyInvestmentReturn = monthlyInvestmentReturn * 12;
    const projectedYearlyInvestmentGrowth = investmentContrib * 12 * (1 + returnRate);
    
    const savingsRate = totalIncome > 0 ? (netCashFlow / totalIncome) * 100 : 0;
    const allocationRate = totalIncome > 0 ? (totalAllocations / totalIncome) * 100 : 0;
    
    const emergencyFundGoal = totalExpenses * 3;
    const currentSavingsNum = parseFloat(currentSavings) || 0;
    const emergencyFundGap = emergencyFundGoal - currentSavingsNum;
    
    let monthsToEmergencyGoal = null;
    if (savingsContrib > 0 && emergencyFundGap > 0) {
      monthsToEmergencyGoal = Math.ceil(emergencyFundGap / savingsContrib);
    }
    
    const yearlyGoalNum = parseFloat(yearlySavingsGoal) || 0;
    const requiredMonthlySaving = yearlyGoalNum / 12;
    const yearlyGoalGap = requiredMonthlySaving - savingsContrib;
    
    // Expense breakdown by category
    const expensesByCategory = {};
    expenses.forEach(e => {
      expensesByCategory[e.category] = (expensesByCategory[e.category] || 0) + e.amount;
    });
    
    // Calculate expense percentages
    const expensePercentages = {};
    Object.entries(expensesByCategory).forEach(([cat, amount]) => {
      expensePercentages[cat] = totalIncome > 0 ? (amount / totalIncome) * 100 : 0;
    });
    
    const newResults = {
      totalIncome,
      totalExpenses,
      netCashFlow,
      savingsContribution: savingsContrib,
      investmentContribution: investmentContrib,
      totalAllocations,
      remainingCashFlow,
      monthlyInvestmentReturn,
      yearlyInvestmentReturn,
      projectedYearlyInvestmentGrowth,
      savingsRate,
      allocationRate,
      emergencyFundGoal,
      emergencyFundGap,
      monthsToEmergencyGoal,
      yearlyGoal: yearlyGoalNum,
      requiredMonthlySaving,
      yearlyGoalGap,
      currentSavings: currentSavingsNum,
      expensesByCategory,
      expensePercentages,
      investmentReturnRate: returnRate * 100
    };
    
    setResults(newResults);
    
    // Generate AI analysis
    generateAnalysis(newResults);
    
    // Calculate gamification score
    calculateScore(newResults);
  }, [incomes, expenses, currentSavings, monthlySavingsContribution, monthlyInvestmentContribution, investmentReturnRate, yearlySavingsGoal]);
  
  // Generate AI Analysis (Rule-based)
  const generateAnalysis = (r) => {
    const strengths = [];
    const weaknesses = [];
    const actions = [];
    let riskLevel = 'LOW';
    
    // Analyze savings rate
    if (r.savingsRate >= 20) {
      strengths.push(`Excellent potential savings rate of ${r.savingsRate.toFixed(1)}% - well above the 20% benchmark`);
    } else if (r.savingsRate >= 10) {
      strengths.push(`Moderate potential savings rate of ${r.savingsRate.toFixed(1)}% - on track for growth`);
    } else if (r.savingsRate > 0) {
      weaknesses.push(`Low potential savings rate of ${r.savingsRate.toFixed(1)}% - target is 20%`);
      actions.push(`Increase savings by reducing expenses by $${((r.totalIncome * 0.2) - r.netCashFlow).toFixed(2)}/month to reach 20%`);
    } else {
      weaknesses.push(`Negative cash flow of -$${Math.abs(r.netCashFlow).toFixed(2)} - spending exceeds income`);
      riskLevel = 'HIGH';
    }
    
    // Analyze allocations
    if (r.allocationRate >= 30) {
      strengths.push(`Strong allocation rate of ${r.allocationRate.toFixed(1)}% toward savings and investments`);
    } else if (r.allocationRate >= 15) {
      strengths.push(`Moderate allocation rate of ${r.allocationRate.toFixed(1)}% toward savings and investments`);
    } else if (r.allocationRate > 0) {
      weaknesses.push(`Low allocation rate of ${r.allocationRate.toFixed(1)}% - consider increasing contributions`);
    }
    
    // Analyze investment returns
    if (r.investmentContribution > 0) {
      strengths.push(`Monthly investment of $${r.investmentContribution.toFixed(2)} generating estimated $${r.monthlyInvestmentReturn.toFixed(2)}/month return`);
      if (r.yearlyInvestmentReturn > 0) {
        actions.push(`Your investments could generate ~$${r.yearlyInvestmentReturn.toFixed(2)} in returns over 12 months`);
      }
    } else {
      weaknesses.push('No investment allocation - missing opportunity for compound growth');
      actions.push('Consider allocating a portion of your cash flow to investments for potential returns');
    }
    
    // Analyze emergency fund
    const emergencyMonths = r.currentSavings / (r.totalExpenses || 1);
    if (emergencyMonths >= 3) {
      strengths.push(`Strong emergency fund covering ${emergencyMonths.toFixed(1)} months of expenses`);
    } else if (emergencyMonths >= 1) {
      weaknesses.push(`Emergency fund only covers ${emergencyMonths.toFixed(1)} months - target is 3 months`);
      if (riskLevel === 'LOW') riskLevel = 'MEDIUM';
    } else {
      weaknesses.push(`Insufficient emergency fund - less than 1 month of expenses covered`);
      actions.push(`Build emergency fund to $${r.emergencyFundGoal.toFixed(2)} (3 months of expenses)`);
      riskLevel = 'HIGH';
    }
    
    // Analyze cash flow after allocations
    if (r.remainingCashFlow > 0) {
      strengths.push(`$${r.remainingCashFlow.toFixed(2)} remaining after all allocations - financial flexibility`);
    } else if (r.remainingCashFlow < 0) {
      weaknesses.push(`Allocations exceed cash flow by $${Math.abs(r.remainingCashFlow).toFixed(2)} - reduce contributions or increase income`);
      riskLevel = 'HIGH';
    }
    
    // Analyze housing costs
    const housingPercent = r.expensePercentages['Housing'] || r.expensePercentages['Rent'] || 0;
    if (housingPercent > 30) {
      weaknesses.push(`Housing costs at ${housingPercent.toFixed(1)}% of income - exceeds 30% benchmark`);
      const housingAmount = r.expensesByCategory['Housing'] || r.expensesByCategory['Rent'] || 0;
      const targetHousing = r.totalIncome * 0.30;
      actions.push(`Reduce housing costs by $${(housingAmount - targetHousing).toFixed(2)}/month to reach 30% benchmark`);
    } else if (housingPercent > 0) {
      strengths.push(`Housing costs well-managed at ${housingPercent.toFixed(1)}% of income`);
    }
    
    // Analyze yearly goal progress
    if (r.yearlyGoal > 0) {
      if (r.yearlyGoalGap <= 0) {
        strengths.push(`On track to exceed yearly savings goal of $${r.yearlyGoal.toLocaleString()}`);
      } else {
        weaknesses.push(`Behind on yearly goal - need additional $${r.yearlyGoalGap.toFixed(2)}/month in savings`);
        actions.push(`Increase monthly savings by $${r.yearlyGoalGap.toFixed(2)} to hit yearly goal of $${r.yearlyGoal.toLocaleString()}`);
      }
    }
    
    // Ensure minimum points
    if (strengths.length < 3) {
      if (r.totalIncome > 0) strengths.push('Tracking income sources systematically');
      if (expenses.length > 0) strengths.push('Actively monitoring and categorizing expenses');
      if (r.currentSavings > 0) strengths.push('Have started building savings foundation');
    }
    
    if (actions.length < 3) {
      actions.push('Review largest expense category for potential 10-15% reduction');
      actions.push('Set up automatic transfers to savings and investment accounts');
    }
    
    setAnalysis({
      strengths: strengths.slice(0, 5),
      weaknesses: weaknesses.slice(0, 5),
      actions: actions.slice(0, 5),
      riskLevel
    });
  };
  
  // Calculate gamification score
  const calculateScore = (r) => {
    let totalScore = 0;
    
    // Savings Rate (30% weight)
    const savingsRateScore = Math.min((r.savingsRate / 20) * 100, 100);
    totalScore += savingsRateScore * 0.30;
    
    // Emergency Fund Progress (25% weight)
    const emergencyProgress = Math.min((r.currentSavings / r.emergencyFundGoal) * 100, 100);
    totalScore += emergencyProgress * 0.25;
    
    // Cash Flow Stability (20% weight)
    const cashFlowScore = r.netCashFlow >= 0 ? Math.min((r.netCashFlow / (r.totalIncome * 0.2)) * 100, 100) : 0;
    totalScore += cashFlowScore * 0.20;
    
    // Allocation Rate (15% weight) - rewards consistent saving/investing
    const allocationScore = Math.min((r.allocationRate / 30) * 100, 100);
    totalScore += allocationScore * 0.15;
    
    // Expense Control (10% weight)
    const expenseRatio = r.totalIncome > 0 ? (r.totalExpenses / r.totalIncome) * 100 : 100;
    const expenseControlScore = Math.max(100 - (expenseRatio - 70), 0);
    totalScore += Math.min(expenseControlScore, 100) * 0.10;
    
    totalScore = Math.round(totalScore);
    
    // Get level
    const level = LEVELS.find(l => totalScore >= l.min && totalScore <= l.max) || LEVELS[0];
    const progressToNext = level.level < 5 
      ? ((totalScore - level.min) / (level.max - level.min)) * 100 
      : 100;
    
    setScore({
      total: totalScore,
      level: level.level,
      levelName: level.name,
      levelIcon: level.icon,
      levelColor: level.color,
      progressToNext: Math.round(progressToNext),
      breakdown: {
        savingsRate: Math.round(savingsRateScore),
        emergencyFund: Math.round(emergencyProgress),
        cashFlow: Math.round(cashFlowScore),
        allocation: Math.round(allocationScore),
        expenseControl: Math.round(expenseControlScore)
      }
    });
  };
  
  // Generate improvement simulations
  const generateSimulations = () => {
    if (!results) return [];
    
    const simulations = [];
    
    // Top 3 largest expenses reduction
    const sortedExpenses = [...expenses].sort((a, b) => b.amount - a.amount).slice(0, 3);
    sortedExpenses.forEach(exp => {
      const reduction = exp.amount * 0.15; // 15% reduction
      const newCashFlow = results.netCashFlow + reduction;
      const newMonthsToEmergency = results.emergencyFundGap > 0 && results.savingsContribution + reduction > 0
        ? Math.ceil(results.emergencyFundGap / (results.savingsContribution + reduction))
        : null;
      
      simulations.push({
        type: 'expense_reduction',
        title: `Reduce "${exp.name}" by 15%`,
        change: `-$${reduction.toFixed(2)}/month`,
        impact: `+$${reduction.toFixed(2)} available for allocation`,
        detail: newMonthsToEmergency 
          ? `Emergency fund could be reached ${results.monthsToEmergencyGoal - newMonthsToEmergency} months faster`
          : 'Improved cash flow stability'
      });
    });
    
    // Income increase simulation
    const incomeIncrease = 500;
    const newIncomeTotal = results.totalIncome + incomeIncrease;
    const newCashFlowWithIncome = results.netCashFlow + incomeIncrease;
    
    simulations.push({
      type: 'income_increase',
      title: `Income increase of $${incomeIncrease}/month`,
      change: `+$${incomeIncrease}/month`,
      impact: `Could allocate additional $${incomeIncrease} to savings/investments`,
      detail: `New potential savings rate: ${((newCashFlowWithIncome / newIncomeTotal) * 100).toFixed(1)}%`
    });
    
    // Investment boost simulation
    if (results.investmentContribution > 0) {
      const doubleInvestment = results.investmentContribution * 2;
      const doubleReturn = doubleInvestment * (results.investmentReturnRate / 100);
      
      simulations.push({
        type: 'investment_boost',
        title: `Double investment to $${doubleInvestment.toFixed(2)}/month`,
        change: `+$${results.investmentContribution.toFixed(2)}/month`,
        impact: `Monthly returns: $${doubleReturn.toFixed(2)} (vs $${results.monthlyInvestmentReturn.toFixed(2)})`,
        detail: `Yearly returns: $${(doubleReturn * 12).toFixed(2)}`
      });
    }
    
    return simulations;
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const categories = planType === 'business' ? BUSINESS_CATEGORIES : INDIVIDUAL_CATEGORIES;
  
  // Disclaimer screen
  if (!disclaimerAccepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 flex items-center justify-center">
        <div className="max-w-lg w-full">
          {/* Back button */}
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bot
          </Button>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Important Disclaimer</h2>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-yellow-200 text-sm leading-relaxed">
                <strong className="text-yellow-300">Educational Purpose Only</strong>
                <br /><br />
                This Financial Game Planner is provided strictly for <strong>educational and entertainment purposes only</strong>. 
                It is <strong>NOT financial advice</strong> and should not be treated as such.
                <br /><br />
                The calculations, projections, and analysis provided are based on the information you enter and general 
                financial principles. They do not take into account your complete financial situation, risk tolerance, 
                or specific circumstances.
                <br /><br />
                <strong>Investment returns are not guaranteed.</strong> Any return projections shown are hypothetical 
                and actual results may vary significantly.
                <br /><br />
                Always consult with a qualified financial advisor before making any financial decisions.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={acceptDisclaimer}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                I Understand - Continue
              </Button>
              
              <Button
                onClick={onBack}
                variant="ghost"
                className="w-full text-white/60 hover:text-white hover:bg-white/10"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Welcome screen - plan type selection
  if (!planType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-2xl mx-auto pt-10">
          {/* Back button */}
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-white/70 hover:text-white hover:bg-white/10 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Bot
          </Button>
          
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome to the Smart Financial Game Planner
              </h1>
              <p className="text-purple-200 text-lg">Your gamified financial goal tracker</p>
              <p className="text-yellow-400/80 text-xs mt-2">For educational purposes only</p>
            </div>
            
            <div className="space-y-4">
              <p className="text-white/80 text-center text-lg mb-6">Is this plan for:</p>
              
              <button
                onClick={() => setPlanType('individual')}
                className="w-full p-6 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-xl hover:from-blue-500/30 hover:to-cyan-500/30 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-500/30 rounded-xl flex items-center justify-center group-hover:bg-blue-500/40">
                    <User className="w-7 h-7 text-blue-300" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white">Individual</h3>
                    <p className="text-blue-200 text-sm">Personal finance: salary, rent, groceries, subscriptions</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => setPlanType('business')}
                className="w-full p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-xl hover:from-purple-500/30 hover:to-pink-500/30 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-purple-500/30 rounded-xl flex items-center justify-center group-hover:bg-purple-500/40">
                    <Building2 className="w-7 h-7 text-purple-300" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white">Business</h3>
                    <p className="text-purple-200 text-sm">Business finance: revenue, payroll, operating costs</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your financial plan...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/30 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Bot
            </Button>
            <div className="h-6 w-px bg-white/20"></div>
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-yellow-400" />
              <span className="font-bold text-white">Financial Game Planner</span>
              <span className="text-xs bg-purple-500/30 text-purple-200 px-2 py-0.5 rounded-full">
                {planType === 'business' ? 'Business' : 'Personal'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={saveData}
              disabled={saving}
              size="sm"
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={resetPlan}
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Educational Disclaimer Banner */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <p className="text-yellow-200 text-xs">For educational purposes only. Not financial advice. Investment returns are not guaranteed.</p>
        </div>
        
        {/* Score Card */}
        {score && (
          <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-3xl shadow-lg">
                  {score.levelIcon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-white">{score.total}</span>
                    <span className="text-yellow-300 text-lg">/100</span>
                  </div>
                  <p className={`font-semibold ${score.levelColor}`}>
                    Level {score.level}: {score.levelName}
                  </p>
                </div>
              </div>
              
              <div className="flex-1 max-w-md">
                <div className="flex justify-between text-sm text-white/70 mb-1">
                  <span>Progress to Level {Math.min(score.level + 1, 5)}</span>
                  <span>{score.progressToNext}%</span>
                </div>
                <div className="h-3 bg-black/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${score.progressToNext}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-5 gap-2 text-center">
                {[
                  { label: 'Savings', value: score.breakdown.savingsRate, icon: PiggyBank },
                  { label: 'Emergency', value: score.breakdown.emergencyFund, icon: Shield },
                  { label: 'Cash Flow', value: score.breakdown.cashFlow, icon: TrendingUp },
                  { label: 'Allocation', value: score.breakdown.allocation, icon: Target },
                  { label: 'Control', value: score.breakdown.expenseControl, icon: Wallet },
                ].map((item, idx) => (
                  <div key={idx} className="bg-white/10 rounded-lg p-2">
                    <item.icon className="w-3 h-3 text-white/60 mx-auto mb-1" />
                    <p className="text-sm font-bold text-white">{item.value}%</p>
                    <p className="text-[10px] text-white/50">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Income Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <button
              onClick={() => toggleSection('income')}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/30 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">
                    {planType === 'business' ? 'Revenue Sources' : 'Income Sources'}
                  </h3>
                  <p className="text-sm text-green-400 font-medium">
                    Total: ${incomes.reduce((sum, i) => sum + i.amount, 0).toLocaleString()}/month
                  </p>
                </div>
              </div>
              {expandedSections.income ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            
            {expandedSections.income && (
              <div className="p-4 pt-0 space-y-3">
                {/* Income list */}
                {incomes.map(income => (
                  <div key={income.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-white">{income.name}</p>
                      <p className="text-xs text-white/50">{income.type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 font-bold">${income.amount.toLocaleString()}</span>
                      <button onClick={() => removeIncome(income.id)} className="p-1 hover:bg-red-500/20 rounded">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Add income form */}
                <div className="bg-white/5 rounded-lg p-3 space-y-2">
                  <Input
                    placeholder={planType === 'business' ? 'Revenue source name' : 'Income source name'}
                    value={newIncome.name}
                    onChange={(e) => setNewIncome({ ...newIncome, name: e.target.value })}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={newIncome.amount}
                      onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 flex-1"
                    />
                    <select
                      value={newIncome.type}
                      onChange={(e) => setNewIncome({ ...newIncome, type: e.target.value })}
                      className="bg-slate-800 border border-white/20 text-white rounded-md px-3"
                    >
                      <option value="Fixed">Fixed</option>
                      <option value="Variable">Variable</option>
                    </select>
                    <Button onClick={addIncome} size="sm" className="bg-green-500 hover:bg-green-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Expenses Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <button
              onClick={() => toggleSection('expenses')}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/30 rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Monthly Expenses</h3>
                  <p className="text-sm text-red-400 font-medium">
                    Total: ${expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString()}/month
                  </p>
                </div>
              </div>
              {expandedSections.expenses ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            
            {expandedSections.expenses && (
              <div className="p-4 pt-0 space-y-3 max-h-96 overflow-y-auto">
                {/* Expense list */}
                {expenses.map(expense => (
                  <div key={expense.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-white">{expense.name}</p>
                      <p className="text-xs text-white/50">{expense.category} • {expense.type}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-400 font-bold">${expense.amount.toLocaleString()}</span>
                      <button onClick={() => removeExpense(expense.id)} className="p-1 hover:bg-red-500/20 rounded">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Add expense form */}
                <div className="bg-white/5 rounded-lg p-3 space-y-2">
                  <Input
                    placeholder="Expense name"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  />
                  <div className="flex gap-2">
                    <select
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                      className="bg-slate-800 border border-white/20 text-white rounded-md px-3 flex-1"
                      style={{ color: 'white' }}
                    >
                      <option value="" className="bg-slate-800 text-white">Select Category...</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat} className="bg-slate-800 text-white">{cat}</option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 w-28"
                    />
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newExpense.type}
                      onChange={(e) => setNewExpense({ ...newExpense, type: e.target.value })}
                      className="bg-slate-800 border border-white/20 text-white rounded-md px-3 flex-1"
                    >
                      <option value="Fixed" className="bg-slate-800 text-white">Fixed</option>
                      <option value="Variable" className="bg-slate-800 text-white">Variable</option>
                    </select>
                    <Button onClick={addExpense} className="bg-red-500 hover:bg-red-600 flex-1">
                      <Plus className="w-4 h-4 mr-1" /> Add Expense
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Cash Flow Allocation Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
          <button
            onClick={() => toggleSection('allocation')}
            className="w-full p-4 flex items-center justify-between hover:bg-white/5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                <PiggyBank className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">Savings, Investments & Goals</h3>
                <p className="text-sm text-blue-400">Allocate your monthly cash flow</p>
              </div>
            </div>
            {expandedSections.allocation ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
          </button>
          
          {expandedSections.allocation && (
            <div className="p-4 pt-0">
              {/* Current Cash Flow Summary */}
              {results && (
                <div className={`rounded-lg p-3 mb-4 ${results.netCashFlow >= 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <p className="text-sm text-white/70">Net Monthly Cash Flow (Income - Expenses)</p>
                  <p className={`text-2xl font-bold ${results.netCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {results.netCashFlow >= 0 ? '+' : ''}${results.netCashFlow.toFixed(2)}
                  </p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Current Savings Balance */}
                <div className="bg-white/5 rounded-lg p-4">
                  <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    Current Savings Balance
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                    <Input
                      type="number"
                      value={currentSavings}
                      onChange={(e) => setCurrentSavings(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pl-7"
                      placeholder="0"
                    />
                  </div>
                </div>
                
                {/* Monthly Savings Contribution */}
                <div className="bg-white/5 rounded-lg p-4">
                  <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                    <PiggyBank className="w-4 h-4 text-green-400" />
                    Monthly Savings
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                    <Input
                      type="number"
                      value={monthlySavingsContribution}
                      onChange={(e) => setMonthlySavingsContribution(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pl-7"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-white/40 mt-1">From your cash flow</p>
                </div>
                
                {/* Monthly Investment Contribution */}
                <div className="bg-white/5 rounded-lg p-4">
                  <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                    <LineChart className="w-4 h-4 text-purple-400" />
                    Monthly Investment
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                    <Input
                      type="number"
                      value={monthlyInvestmentContribution}
                      onChange={(e) => setMonthlyInvestmentContribution(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pl-7"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-white/40 mt-1">For potential returns</p>
                </div>
                
                {/* Investment Return Rate */}
                <div className="bg-white/5 rounded-lg p-4">
                  <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-yellow-400" />
                    Expected Monthly Return
                  </label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={investmentReturnRate}
                      onChange={(e) => setInvestmentReturnRate(e.target.value)}
                      className="bg-white/10 border-white/20 text-white pr-8"
                      placeholder="20"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">%</span>
                  </div>
                  <p className="text-xs text-yellow-400/60 mt-1">*Hypothetical, not guaranteed</p>
                </div>
              </div>
              
              {/* Yearly Savings Goal */}
              <div className="mt-4 bg-white/5 rounded-lg p-4">
                <label className="text-sm text-white/70 mb-2 block flex items-center gap-2">
                  <Target className="w-4 h-4 text-orange-400" />
                  Yearly Savings Goal
                </label>
                <div className="relative max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
                  <Input
                    type="number"
                    value={yearlySavingsGoal}
                    onChange={(e) => setYearlySavingsGoal(e.target.value)}
                    className="bg-white/10 border-white/20 text-white pl-7"
                    placeholder="0"
                  />
                </div>
              </div>
              
              {/* Allocation Summary */}
              {results && results.totalAllocations > 0 && (
                <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-purple-300 mb-2">Allocation Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-xs text-white/50">Total Allocations</p>
                      <p className="text-lg font-bold text-white">${results.totalAllocations.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Remaining Cash Flow</p>
                      <p className={`text-lg font-bold ${results.remainingCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        ${results.remainingCashFlow.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Est. Monthly Return</p>
                      <p className="text-lg font-bold text-yellow-400">${results.monthlyInvestmentReturn.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/50">Est. Yearly Return</p>
                      <p className="text-lg font-bold text-yellow-400">${results.yearlyInvestmentReturn.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Financial Results */}
        {results && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <button
              onClick={() => toggleSection('results')}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Financial Results</h3>
                  <p className={`text-sm font-medium ${results.netCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    Net Cash Flow: {results.netCashFlow >= 0 ? '+' : ''}${results.netCashFlow.toLocaleString()}/month
                  </p>
                </div>
              </div>
              {expandedSections.results ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            
            {expandedSections.results && (
              <div className="p-4 pt-0">
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Savings Rate */}
                  <div className={`rounded-lg p-4 ${results.savingsRate >= 20 ? 'bg-green-500/20 border border-green-400/30' : results.savingsRate >= 10 ? 'bg-yellow-500/20 border border-yellow-400/30' : 'bg-red-500/20 border border-red-400/30'}`}>
                    <p className="text-sm text-white/70 mb-1">Potential Savings Rate</p>
                    <p className={`text-2xl font-bold ${results.savingsRate >= 20 ? 'text-green-400' : results.savingsRate >= 10 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {results.savingsRate.toFixed(1)}%
                    </p>
                    <p className="text-xs text-white/50">Target: 20%+</p>
                  </div>
                  
                  {/* Emergency Fund */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-sm text-white/70 mb-1">3-Month Emergency Fund Goal</p>
                    <p className="text-2xl font-bold text-white">${results.emergencyFundGoal.toFixed(2)}</p>
                    <p className={`text-xs ${results.emergencyFundGap <= 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                      {results.emergencyFundGap <= 0 ? `Surplus: $${Math.abs(results.emergencyFundGap).toFixed(2)}` : `Gap: $${results.emergencyFundGap.toFixed(2)}`}
                    </p>
                  </div>
                  
                  {/* Months to Emergency Goal */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-sm text-white/70 mb-1">Months to Emergency Goal</p>
                    <p className="text-2xl font-bold text-white">
                      {results.monthsToEmergencyGoal !== null ? `${results.monthsToEmergencyGoal} months` : results.emergencyFundGap <= 0 ? 'Achieved!' : 'N/A'}
                    </p>
                    {results.savingsContribution <= 0 && results.emergencyFundGap > 0 && (
                      <p className="text-xs text-red-400">Set monthly savings to calculate</p>
                    )}
                  </div>
                  
                  {/* Yearly Goal */}
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-sm text-white/70 mb-1">Required Monthly for Yearly Goal</p>
                    <p className="text-2xl font-bold text-white">${results.requiredMonthlySaving.toFixed(2)}</p>
                    <p className="text-xs text-white/50">Goal: ${results.yearlyGoal.toLocaleString()}/year</p>
                  </div>
                  
                  {/* Yearly Goal Gap */}
                  <div className={`rounded-lg p-4 ${results.yearlyGoalGap <= 0 ? 'bg-green-500/20 border border-green-400/30' : 'bg-yellow-500/20 border border-yellow-400/30'}`}>
                    <p className="text-sm text-white/70 mb-1">Yearly Goal Status</p>
                    <p className={`text-2xl font-bold ${results.yearlyGoalGap <= 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                      {results.yearlyGoalGap <= 0 ? 'On Track!' : `Need $${results.yearlyGoalGap.toFixed(2)}/mo more`}
                    </p>
                  </div>
                  
                  {/* Investment Projection */}
                  {results.investmentContribution > 0 && (
                    <div className="bg-purple-500/20 border border-purple-400/30 rounded-lg p-4">
                      <p className="text-sm text-white/70 mb-1">12-Month Investment Projection*</p>
                      <p className="text-2xl font-bold text-purple-400">
                        ${results.projectedYearlyInvestmentGrowth.toFixed(2)}
                      </p>
                      <p className="text-xs text-yellow-400/60">*Hypothetical at {results.investmentReturnRate}% monthly</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* AI Analysis */}
        {analysis && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <button
              onClick={() => toggleSection('analysis')}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/30 rounded-lg flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Financial Analysis</h3>
                  <p className="text-sm">
                    <span className={`font-medium ${analysis.riskLevel === 'LOW' ? 'text-green-400' : analysis.riskLevel === 'MEDIUM' ? 'text-yellow-400' : 'text-red-400'}`}>
                      Risk Level: {analysis.riskLevel}
                    </span>
                  </p>
                </div>
              </div>
              {expandedSections.analysis ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            
            {expandedSections.analysis && (
              <div className="p-4 pt-0 space-y-4">
                {/* Strengths */}
                <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-4">
                  <h4 className="font-semibold text-green-400 flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5" /> Strengths
                  </h4>
                  <ul className="space-y-2">
                    {analysis.strengths.map((s, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                        <span className="text-green-400 mt-0.5">✓</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Weaknesses */}
                {analysis.weaknesses.length > 0 && (
                  <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4">
                    <h4 className="font-semibold text-red-400 flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5" /> Areas for Improvement
                    </h4>
                    <ul className="space-y-2">
                      {analysis.weaknesses.map((w, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                          <span className="text-red-400 mt-0.5">!</span>
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Actions */}
                <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-400 flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5" /> Suggested Actions
                  </h4>
                  <ul className="space-y-2">
                    {analysis.actions.map((a, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/80 text-sm">
                        <span className="text-blue-400 mt-0.5 font-bold">{idx + 1}.</span>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Improvement Simulations */}
        {results && expenses.length > 0 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            <button
              onClick={() => toggleSection('simulations')}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/30 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-orange-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-white">Smart Improvement Simulations</h3>
                  <p className="text-sm text-orange-400">See how changes could impact your goals</p>
                </div>
              </div>
              {expandedSections.simulations ? <ChevronUp className="w-5 h-5 text-white/50" /> : <ChevronDown className="w-5 h-5 text-white/50" />}
            </button>
            
            {expandedSections.simulations && (
              <div className="p-4 pt-0 space-y-3">
                {generateSimulations().map((sim, idx) => (
                  <div 
                    key={idx} 
                    className={`rounded-lg p-4 border ${
                      sim.type === 'income_increase' 
                        ? 'bg-green-500/10 border-green-400/30' 
                        : sim.type === 'investment_boost'
                        ? 'bg-purple-500/10 border-purple-400/30'
                        : 'bg-orange-500/10 border-orange-400/30'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h5 className="font-semibold text-white">{sim.title}</h5>
                        <p className={`text-sm ${sim.type === 'income_increase' ? 'text-green-400' : sim.type === 'investment_boost' ? 'text-purple-400' : 'text-orange-400'}`}>
                          {sim.change}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium text-sm">{sim.impact}</p>
                        <p className="text-xs text-white/60">{sim.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialGamePlanner;
